import { supabaseAdmin as supabase } from '../../../lib/supabase.js';

class ClassSubjectQuotaService {
  // Get all quota data for a specific page
  async getQuotaData(pageNumber = 0, tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      const { data, error } = await supabase
        .from('saas_myclass_class_subject_item_load_quota')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('page_number', pageNumber)
        .order('subject_code')
        .order('class_code');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching quota data:', error);
      return [];
    }
  }

  // Get quota data organized as a grid (subjects x classes)
  async getQuotaGrid(pageNumber = 0, tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      const data = await this.getQuotaData(pageNumber, tenantId);
      
      // Organize data into grid format
      const subjects = [...new Set(data.map(item => item.subject_code))].sort();
      const classes = [...new Set(data.map(item => item.class_code))].sort();
      
      const grid = {};
      subjects.forEach(subject => {
        grid[subject] = {};
        classes.forEach(classCode => {
          const item = data.find(d => d.subject_code === subject && d.class_code === classCode);
          grid[subject][classCode] = item || null;
        });
      });
      
      return {
        subjects,
        classes,
        grid,
        rawData: data
      };
    } catch (error) {
      console.error('Error organizing quota grid:', error);
      return { subjects: [], classes: [], grid: {}, rawData: [] };
    }
  }

  // Update quota value for a specific subject-class combination
  async updateQuotaValue(subjectCode, classCode, newQuotaValue, pageNumber = 0, tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      // Parse the quota value to extract components
      const quotaParts = this.parseQuotaValue(newQuotaValue);
      
      const { data, error } = await supabase
        .from('saas_myclass_class_subject_item_load_quota')
        .update({
          quota_value: newQuotaValue,
          quota_group: quotaParts.group,
          quota_sessions: quotaParts.sessions,
          quota_sub_id: quotaParts.subId,
          quota_type: quotaParts.type,
          updated_at: new Date().toISOString()
        })
        .eq('tenant_id', tenantId)
        .eq('subject_code', subjectCode)
        .eq('class_code', classCode)
        .eq('page_number', pageNumber)
        .select();

      if (error) throw error;
      return data[0];
    } catch (error) {
      console.error('Error updating quota value:', error);
      throw error;
    }
  }

  // Create new quota entry
  async createQuotaEntry(quotaData, tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      const quotaParts = this.parseQuotaValue(quotaData.quota_value);
      
      const { data, error } = await supabase
        .from('saas_myclass_class_subject_item_load_quota')
        .insert([{
          ...quotaData,
          tenant_id: tenantId,
          quota_group: quotaParts.group,
          quota_sessions: quotaParts.sessions,
          quota_sub_id: quotaParts.subId,
          quota_type: quotaParts.type,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select();

      if (error) throw error;
      return data[0];
    } catch (error) {
      console.error('Error creating quota entry:', error);
      throw error;
    }
  }

  // Delete quota entry
  async deleteQuotaEntry(id, tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      const { error } = await supabase
        .from('saas_myclass_class_subject_item_load_quota')
        .delete()
        .eq('id', id)
        .eq('tenant_id', tenantId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting quota entry:', error);
      throw error;
    }
  }

  // Make Split operation (分班〔一班分拆為兩班〕)
  async makeSplit(subjectCode, classCode, newSessions, pageNumber = 1, tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      const quotaValue = `d ${newSessions}`;
      const quotaData = {
        subject_code: subjectCode,
        class_code: classCode,
        subject_name: this.getSubjectName(subjectCode),
        class_name: classCode,
        quota_value: quotaValue,
        quota_type: 'split',
        page_number: pageNumber,
        is_split: true
      };
      
      return await this.createQuotaEntry(quotaData, tenantId);
    } catch (error) {
      console.error('Error making split:', error);
      throw error;
    }
  }

  // Make Combined operation (合班〔一班分拆為兩班〕)
  async makeCombined(subjectCode, classCode, newSessions, pageNumber = 1, tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      const quotaValue = `d ${newSessions} a`;
      const quotaData = {
        subject_code: subjectCode,
        class_code: classCode,
        subject_name: this.getSubjectName(subjectCode),
        class_name: classCode,
        quota_value: quotaValue,
        quota_type: 'combined',
        page_number: pageNumber,
        is_combined: true
      };
      
      return await this.createQuotaEntry(quotaData, tenantId);
    } catch (error) {
      console.error('Error making combined:', error);
      throw error;
    }
  }

  // Make Atomic operation (分班〔一班分由兩個教師授課〕)
  async makeAtomic(subjectCode, classCode, newSessions, pageNumber = 1, tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      const quotaValue = `a ${newSessions}`;
      const quotaData = {
        subject_code: subjectCode,
        class_code: classCode,
        subject_name: this.getSubjectName(subjectCode),
        class_name: classCode,
        quota_value: quotaValue,
        quota_type: 'atomic',
        page_number: pageNumber,
        is_atomic: true
      };
      
      return await this.createQuotaEntry(quotaData, tenantId);
    } catch (error) {
      console.error('Error making atomic:', error);
      throw error;
    }
  }

  // Modify quota size for existing entries
  async modifyQuotaSize(quotaId, newSessions, tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      // First get the current quota data
      const { data: currentData, error: fetchError } = await supabase
        .from('saas_myclass_class_subject_item_load_quota')
        .select('*')
        .eq('id', quotaId)
        .eq('tenant_id', tenantId)
        .single();

      if (fetchError) throw fetchError;

      // Update the quota value with new sessions
      const quotaParts = this.parseQuotaValue(currentData.quota_value);
      const newQuotaValue = `${quotaParts.group} ${newSessions}${quotaParts.subId ? ' ' + quotaParts.subId : ''}`;

      const { data, error } = await supabase
        .from('saas_myclass_class_subject_item_load_quota')
        .update({
          quota_value: newQuotaValue,
          quota_sessions: newSessions,
          updated_at: new Date().toISOString()
        })
        .eq('id', quotaId)
        .eq('tenant_id', tenantId)
        .select();

      if (error) throw error;
      return data[0];
    } catch (error) {
      console.error('Error modifying quota size:', error);
      throw error;
    }
  }

  // Delete chunk (delete quota entry)
  async deleteChunk(quotaId, tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      return await this.deleteQuotaEntry(quotaId, tenantId);
    } catch (error) {
      console.error('Error deleting chunk:', error);
      throw error;
    }
  }

  // Parse quota value string (e.g., "d 6", "a 2", "b 6 a")
  parseQuotaValue(quotaValue) {
    const parts = quotaValue.trim().split(' ');
    return {
      group: parts[0] || 'd',
      sessions: parseInt(parts[1]) || 0,
      subId: parts[2] || null,
      type: this.determineQuotaType(parts[0], parts[2])
    };
  }

  // Determine quota type based on group and subId
  determineQuotaType(group, subId) {
    if (subId) return 'combined';
    if (group === 'a') return 'atomic';
    if (group === 'b') return 'split';
    return 'normal';
  }

  // Get subject name from code
  getSubjectName(subjectCode) {
    const subjectNames = {
      'BIA': 'Business Intelligence',
      'BIO': 'Biology',
      'CCP': 'Computer Programming',
      'CH': 'Chemistry',
      'CHA': 'Chinese Art',
      'CHE': 'Chinese History',
      'CHI': 'Chinese',
      'RCH': 'Chinese Tutoring',
      'CL&': 'Class Leadership',
      'CMA': 'Computer Mathematics',
      'CS': 'Computer Science',
      'CSA': 'Computer Science Advanced',
      'D&T': 'Design & Technology',
      'ECP': 'Economics',
      'ENG': 'English'
    };
    return subjectNames[subjectCode] || subjectCode;
  }

  // Get statistics for quota data
  async getQuotaStatistics(pageNumber = 0, tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      const data = await this.getQuotaData(pageNumber, tenantId);
      
      const stats = {
        totalEntries: data.length,
        totalSessions: data.reduce((sum, item) => sum + (item.quota_sessions || 0), 0),
        byType: {},
        byGroup: {},
        negativeBalances: data.filter(item => item.quota_sessions < 0).length
      };

      data.forEach(item => {
        const type = item.quota_type || 'normal';
        const group = item.quota_group || 'd';
        
        if (!stats.byType[type]) {
          stats.byType[type] = { count: 0, totalSessions: 0 };
        }
        stats.byType[type].count++;
        stats.byType[type].totalSessions += item.quota_sessions || 0;
        
        if (!stats.byGroup[group]) {
          stats.byGroup[group] = { count: 0, totalSessions: 0 };
        }
        stats.byGroup[group].count++;
        stats.byGroup[group].totalSessions += item.quota_sessions || 0;
      });

      return stats;
    } catch (error) {
      console.error('Error getting quota statistics:', error);
      return {
        totalEntries: 0,
        totalSessions: 0,
        byType: {},
        byGroup: {},
        negativeBalances: 0
      };
    }
  }
}

export default new ClassSubjectQuotaService();
